/**
 * Generalized "When / Who / Channel" rules config for automation templates.
 *
 * This is the single, neutral home for the messaging-template rule vocabulary —
 * shared by both the email-templates visualizer (`components/email-templates/`)
 * and the Automations editor (`components/dashboard/messages/automations/`) so
 * the two read and behave identically. It intentionally mirrors the vocabulary
 * of the Automations editor (`automations.ts` — `TriggerKind`, `AudienceKind`,
 * `AUDIENCE_LABELS`, `describeTiming`/`describeConsequence`).
 *
 * It carries its own audiences (e.g. the paid/free subscriber split the
 * automations registry can't yet express) so the email-template domain can grow
 * independently of the automations feature.
 *
 * Any `ExportedTemplate` can opt in via its optional `rules` field; templates
 * without rules simply don't render a rules card.
 */

/** Who an automated send targets. Superset of the automations audiences. */
export type TemplateAudience =
  | "all-subscribers"
  | "paid-subscribers"
  | "free-subscribers"
  | "fans-near-venue"
  | "waitlist"
  | "self";

/** Plain-language label shown in the "Who" row. */
export const TEMPLATE_AUDIENCE_LABELS: Record<TemplateAudience, string> = {
  "all-subscribers": "All subscribers",
  "paid-subscribers": "Paid subscribers",
  "free-subscribers": "Free subscribers",
  "fans-near-venue": "Fans near the venue",
  waitlist: "Waitlist fans",
  self: "Just you",
};

/**
 * How a send is triggered. "on-event" fires the moment something happens (a new
 * episode published); "before-show" fires a chosen offset ahead of a show;
 * "recurring" fires on a fixed weekly cadence. Mirrors the automations
 * `TriggerKind`.
 */
export type TemplateTriggerKind = "on-event" | "before-show" | "recurring";

/**
 * Which channel(s) an automated send goes out on. "email_and_sms" mirrors the
 * unified-messaging `email_and_sms` preference — the recipient gets whichever
 * channel(s) they're reachable on.
 */
export type TemplateChannel = "email" | "sms" | "email_and_sms";

/** Plain-language label shown in the "Channel" row. */
export const TEMPLATE_CHANNEL_LABELS: Record<TemplateChannel, string> = {
  email: "Email",
  sms: "SMS",
  email_and_sms: "Email & SMS",
};

export interface TemplateRules {
  triggerKind: TemplateTriggerKind;
  /**
   * Plain-language clause completing the trigger sentence. For "on-event" it's
   * the event ("you publish a new podcast episode"); for "before-show" the
   * offset phrase ("2 weeks"); for "recurring" the cadence ("every Monday").
   */
  triggerLabel: string;
  audience: TemplateAudience;
  /**
   * Optional timing qualifier appended to the "When" sentence, e.g.
   * "next available window" → "… — sent in the next available window".
   */
  sendWindow?: string;
  /**
   * Optional channel the send goes out on. When set, the read-only rules card
   * renders a "Channel" row; templates without it render no Channel row.
   */
  channel?: TemplateChannel;
}

/** Human label for the "Who" row. */
export function describeRuleAudience(rules: TemplateRules): string {
  return TEMPLATE_AUDIENCE_LABELS[rules.audience];
}

/** Human label for the "Channel" row, or null when no channel is set. */
export function describeRuleChannel(rules: TemplateRules): string | null {
  return rules.channel ? TEMPLATE_CHANNEL_LABELS[rules.channel] : null;
}

/** Whether the send includes SMS (so an SMS-body sample should be shown). */
export function ruleIncludesSms(rules: TemplateRules): boolean {
  return rules.channel === "sms" || rules.channel === "email_and_sms";
}

/**
 * Collapsed "When" label for the read-only dropdown — the full sentence
 * (`describeRuleTiming`) lives in the hover tooltip. On-event collapses to
 * "Right away"; the other kinds reuse their offset/cadence clause verbatim.
 */
export function describeRuleTriggerShort(rules: TemplateRules): string {
  switch (rules.triggerKind) {
    case "on-event":
      return "Right away";
    case "before-show":
      return rules.triggerLabel;
    case "recurring":
      return rules.triggerLabel;
  }
}

/**
 * One-line "When" sentence, mirroring the automations editor's timing copy.
 * The optional `sendWindow` clause communicates that on-event sends go out in
 * the next batch rather than at a fixed clock time.
 */
export function describeRuleTiming(rules: TemplateRules): string {
  const { triggerKind, triggerLabel, sendWindow } = rules;
  const window = sendWindow ? ` — sent in the ${sendWindow}` : "";
  switch (triggerKind) {
    case "on-event":
      return `Right away when ${triggerLabel}${window}.`;
    case "before-show":
      return `${triggerLabel} before the show${window}.`;
    case "recurring":
      return `${triggerLabel}${window}.`;
  }
}
